
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CidadeHero from "@/components/cidade/CidadeHero";
import CidadeBeneficios from "@/components/cidade/CidadeBeneficios";
import CidadeEstrategia from "@/components/cidade/CidadeEstrategia";
import CidadeFAQ from "@/components/cidade/CidadeFAQ";
import CidadeCTA from "@/components/cidade/CidadeCTA";
import CidadeSEO from "@/components/cidade/CidadeSEO";
import { findCityData, generateFaqItems } from "@/utils/cityMetadata";

interface PreloadedCityData {
  cidade: string;
  cidadeFormatted: string;
  estado: string;
  estadoNome: string;
  faqItems: Array<{
    question: string;
    answer: string;
  }>;
}

const CidadePage = () => {
  const { cidade } = useParams<{ cidade: string }>();
  const navigate = useNavigate();
  const [estadoNome, setEstadoNome] = useState("");
  const [estado, setEstado] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [cidadeFormatted, setCidadeFormatted] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [faqItems, setFaqItems] = useState<Array<{ question: string; answer: string }>>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Verificar se temos dados pré-renderizados
    const loadPrerenderedData = () => {
      try {
        const preloadedDataStr = localStorage.getItem('preloaded-city-data');
        if (preloadedDataStr) {
          const preloadedData: PreloadedCityData = JSON.parse(preloadedDataStr);
          
          // Verificar se os dados pré-renderizados correspondem à cidade atual
          if (preloadedData.cidade === cidade) {
            console.log('Usando dados pré-renderizados para', preloadedData.cidadeFormatted);
            setCidadeFormatted(preloadedData.cidadeFormatted);
            setEstadoNome(preloadedData.estadoNome);
            setEstado(preloadedData.estado);
            setFaqItems(preloadedData.faqItems);
            setIsLoading(false);
            setIsHydrated(true);
            return true; // Indica que usamos dados pré-renderizados
          }
        }
        return false; // Indica que não havia dados pré-renderizados válidos
      } catch (error) {
        console.error('Erro ao carregar dados pré-renderizados:', error);
        return false;
      }
    };

    // Carregar dados normalmente se não temos dados pré-renderizados
    const loadCityData = () => {
      if (!cidade) {
        console.error("No cidade parameter provided");
        setError("Cidade não encontrada");
        setIsLoading(false);
        return;
      }

      try {
        // Buscar dados da cidade
        const cityData = findCityData(cidade);
        
        if (!cityData) {
          console.error(`Cidade não encontrada: ${cidade}`);
          setError(`Cidade "${cidade}" não foi encontrada em nossa base de dados.`);
          setIsLoading(false);
          return;
        }
        
        const { foundState, originalCityName, estadoNome } = cityData;
        
        setEstadoNome(estadoNome);
        setEstado(foundState);
        setCidadeFormatted(originalCityName);
        
        // Generate FAQ items for this city
        const generatedFaqItems = generateFaqItems(originalCityName, estadoNome);
        setFaqItems(generatedFaqItems);
        
        setIsLoading(false);
        
        console.log(`Cidade page loaded for: ${originalCityName}, ${foundState} - ${window.location.pathname}`);
      } catch (err) {
        console.error("Error loading city data:", err);
        setError("Ocorreu um erro ao carregar dados da cidade.");
        setIsLoading(false);
      }
    };

    // Primeiro tentar usar dados pré-renderizados, se não tiver, carregar normalmente
    const usedPrerenderedData = loadPrerenderedData();
    if (!usedPrerenderedData) {
      loadCityData();
    }
  }, [cidade, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg font-medium">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Erro</h2>
        <p className="text-lg">{error}</p>
        <button 
          onClick={() => navigate("/cidades")} 
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          Ver todas as cidades
        </button>
      </div>
    );
  }

  return (
    <>
      <CidadeSEO 
        cidade={cidade!}
        cidadeFormatted={cidadeFormatted}
        estadoNome={estadoNome}
        estado={estado}
        faqItems={faqItems}
      />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main>
          <CidadeHero 
            cidadeFormatted={cidadeFormatted}
            estadoNome={estadoNome}
            estado={estado}
          />

          <CidadeBeneficios cidadeFormatted={cidadeFormatted} />

          <CidadeEstrategia 
            cidadeFormatted={cidadeFormatted}
            estadoNome={estadoNome}
          />

          <CidadeFAQ 
            faqItems={faqItems}
            cidadeFormatted={cidadeFormatted}
            estadoNome={estadoNome}
          />

          <CidadeCTA cidadeFormatted={cidadeFormatted} />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CidadePage;
